import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  LayoutDashboard,
  FileText,
  Building2,
  Users,
  BarChart3,
  ChevronLeft,
  ChevronRight,
  DollarSign,
  Settings,
  UserCog,
  AlertOctagon,
} from 'lucide-react';
import { cn, isRTL } from '../../lib/utils';

interface MenuItem {
  title: string;
  icon: React.ReactNode;
  path: string;
  roles: string[];
}

interface SidebarProps {
  userType: string;
}

const Sidebar: React.FC<SidebarProps> = ({ userType }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t, i18n } = useTranslation();
  const [isCollapsed, setIsCollapsed] = useState(false);
  const rtl = isRTL(i18n.language);

  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth < 1280;
      if (mobile) {
        setIsCollapsed(true);
      }
    };

    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const menuItems: MenuItem[] = [
    // Admin Dashboard
    {
      title: t('menu.dashboard'),
      icon: <LayoutDashboard className="w-5 h-5" />,
      path: '/dashboard',
      roles: ['admin']
    },
    // Company Dashboard (Company Owner & Company Manager)
    {
      title: t('menu.companyDashboard') || 'Company Dashboard',
      icon: <LayoutDashboard className="w-5 h-5" />,
      path: '/company-dashboard',
      roles: ['company_owner', 'company_manager']
    },
    // Complaints (Admin, Company Owner & Company Manager)
    {
      title: t('menu.complaints'),
      icon: <FileText className="w-5 h-5" />,
      path: '/complaints',
      roles: ['admin', 'company_owner', 'company_manager']
    },
    // Company Managers (Company Owner only)
    {
      title: t('menu.managers') || 'Managers',
      icon: <UserCog className="w-5 h-5" />,
      path: '/managers',
      roles: ['company_owner']
    },
    // My Violations (Company Owner & Company Manager)
    {
      title: t('menu.myViolations') || 'خطاياي',
      icon: <AlertOctagon className="w-5 h-5" />,
      path: '/my-violations',
      roles: ['company_owner', 'company_manager']
    },
    // Companies (Admin only)
    {
      title: t('menu.companies'),
      icon: <Building2 className="w-5 h-5" />,
      path: '/companies',
      roles: ['admin']
    },
    // Evaluators (Admin only)
    {
      title: t('menu.evaluators'),
      icon: <Users className="w-5 h-5" />,
      path: '/evaluators',
      roles: ['admin']
    },
    // Deductions (Admin only)
    {
      title: t('menu.deductions') || 'Deductions',
      icon: <DollarSign className="w-5 h-5" />,
      path: '/deductions',
      roles: ['admin']
    },
    // SLA Configuration (Admin only) - HIDDEN FOR NOW
    // {
    //   title: t('menu.slaConfig') || 'SLA Configuration',
    //   icon: <Settings className="w-5 h-5" />,
    //   path: '/sla-config',
    //   roles: ['admin']
    // },
    // Reports (Admin only)
    {
      title: t('menu.reports'),
      icon: <BarChart3 className="w-5 h-5" />,
      path: '/reports',
      roles: ['admin']
    },
    // Evaluator Reports (Admin only)
    {
      title: t('menu.evaluatorReports'),
      icon: <Users className="w-5 h-5" />,
      path: '/evaluator-reports',
      roles: ['admin']
    },
    // Company Performance (Admin only)
    {
      title: t('menu.companyPerformance'),
      icon: <Building2 className="w-5 h-5" />,
      path: '/company-performance',
      roles: ['admin']
    },
  ];

  // Filter menu items based on user role
  const filteredMenuItems = menuItems.filter(item => item.roles.includes(userType));

  return (
    <aside
      className={cn(
        'fixed top-0 h-screen bg-card border-r border-border transition-all duration-300 z-40',
        isCollapsed ? 'w-20' : 'w-64',
        rtl ? 'right-0' : 'left-0'
      )}
    >
      {/* Header */}
      <div className="h-16 flex items-center justify-between px-4 border-b border-border">
        {!isCollapsed && (
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg gradient-brand flex items-center justify-center">
              <span className="text-white font-bold text-lg">ل</span>
            </div>
            <span className="font-bold text-lg bg-gradient-to-r from-brand-500 to-brand-700 bg-clip-text text-transparent">
              {t('app.title').split(' ')[0]}
            </span>
          </div>
        )}
        <button
          onClick={() => setIsCollapsed(!isCollapsed)}
          className="p-1.5 rounded-lg hover:bg-accent transition-colors"
          title={isCollapsed ? t('common.expand') || 'Expand' : t('common.collapse') || 'Collapse'}
        >
          {isCollapsed ? (
            rtl ? <ChevronLeft className="w-5 h-5" /> : <ChevronRight className="w-5 h-5" />
          ) : (
            rtl ? <ChevronRight className="w-5 h-5" /> : <ChevronLeft className="w-5 h-5" />
          )}
        </button>
      </div>

      {/* Navigation */}
      <nav className="p-4 space-y-2 overflow-y-auto" style={{ height: 'calc(100vh - 8rem)' }}>
        {filteredMenuItems.map((item) => {
          const isActive = location.pathname === item.path;
          return (
            <button
              key={item.path}
              onClick={() => navigate(item.path)}
              className={cn(
                'w-full flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all duration-200',
                'hover:bg-accent hover:text-accent-foreground',
                isActive && 'bg-primary text-primary-foreground shadow-md',
                isCollapsed && 'justify-center'
              )}
              title={isCollapsed ? item.title : undefined}
            >
              {item.icon}
              {!isCollapsed && (
                <span className={cn('font-medium', rtl && 'text-right flex-1')}>
                  {item.title}
                </span>
              )}
            </button>
          );
        })}
      </nav>

      {/* Footer Info */}
      {!isCollapsed && (
        <div className="absolute bottom-4 left-4 right-4 p-3 bg-accent/50 rounded-lg">
          <p className="text-xs text-muted-foreground text-center">
            {userType === 'admin'
              ? t('common.adminPanel')
              : userType === 'company_owner'
              ? (t('common.ownerPanel') || 'Owner Panel')
              : (t('common.companyPanel') || 'Company Panel')}
          </p>
          <p className="text-xs text-muted-foreground text-center mt-1">
            v1.0.0
          </p>
        </div>
      )}
    </aside>
  );
};

export default Sidebar;
